<template>
  <div
    class="h-100 px-0"
    :class="{
      'h-0': displayMode === InputFrameMode.modal,
      container: displayMode === InputFrameMode.default,
      'px-2': displayMode === InputFrameMode.containerless,
    }"
  >
    <MobileHeaderTitle v-if="title && displayMode !== InputFrameMode.modal" :title="title" />
    <div class="mainGrid" :class="{ 'h-100': displayMode === InputFrameMode.noFooterPadding }">
      <InputFrameHeader
        v-if="displayMode !== InputFrameMode.modal && !hideHeader"
        :title="title"
        :validation="validation"
        :create-to="createTo"
        :containerless="displayMode === InputFrameMode.containerless"
        :disable-create="disableCreate"
        :wrap-actions="wrapHeaderActions"
        hide-title-on-mobile
        class="px-2"
        :class="{
          doNotPrintMe: hideHeaderOnPrint,
        }"
        @click:hidden-feature="$emit('click:hiddenFeature')"
        @click:create="emit('click:create')"
      >
        <template v-if="$slots.select != null" #select="args">
          <slot name="select" v-bind="args" />
        </template>
        <template v-if="$slots['select:prepend'] != null" #select:prepend>
          <slot name="select:prepend" />
        </template>
        <template v-if="$slots['frame-breadcrumbs'] != null" #frame-breadcrumbs="args">
          <slot name="frame-breadcrumbs" v-bind="args" />
        </template>
        <template v-if="$slots['frame-create'] != null" #frame-create>
          <slot name="frame-create" />
        </template>
        <template v-if="$slots['header-pre'] != null" #header-pre>
          <slot name="header-pre" />
        </template>
        <template v-if="$slots.headerCenter != null" #headerCenter>
          <slot name="headerCenter" />
        </template>
        <template v-if="$slots['header-trailing'] != null" #header-trailing="args">
          <slot name="header-trailing" v-bind="args" />
        </template>
        <template v-if="$slots['header-trailing-after'] != null" #header-trailing-after="args">
          <slot name="header-trailing-after" v-bind="args" />
        </template>
      </InputFrameHeader>
      <div
        :class="{
          stretching: modalInBoardStructure === true,
          NotStretching: modalInBoardStructure === false,
        }"
      >
        <form class="innerGrid paddingForScroll" novalidate @submit.prevent="save">
          <LModal
            v-if="displayMode === InputFrameMode.modal"
            v-model:visible="showModal"
            :title="title"
            size="xxl"
            :ignore-discard-changes-confirmation="footerless || ignoreDiscardChangesConfirmation"
            :show-footer="!footerless"
            :submit="submit"
            propagate-validation
            :pt="{
              header: {
                class: 'pb-0',
              },
              content: {
                class: 'pb-0',
              },
            }"
            dismissable-mask
            hide-validation-summary
            v-bind="$attrs"
          >
            <template #header>
              <InputFrameHeader
                :title="title"
                :validation="validation"
                :create-to="createTo"
                containerless
                :disable-create="disableCreate"
                :wrap-actions="wrapHeaderActions"
                class="w-100"
                :class="{
                  doNotPrintMe: hideHeaderOnPrint,
                }"
                @click:hidden-feature="$emit('click:hiddenFeature')"
                @click:create="emit('click:create')"
              >
                <template v-if="$slots.select != null" #select="args">
                  <slot name="select" v-bind="args" />
                </template>
                <template v-if="$slots['frame-breadcrumbs'] != null" #frame-breadcrumbs="args">
                  <slot name="frame-breadcrumbs" v-bind="args" />
                </template>
                <template v-if="$slots['frame-create'] != null" #frame-create>
                  <slot name="frame-create" />
                </template>
                <template v-if="$slots['header-pre'] != null" #header-pre>
                  <slot name="header-pre" />
                </template>
                <template v-if="$slots.headerCenter != null" #headerCenter>
                  <slot name="headerCenter" />
                </template>
                <template v-if="$slots['header-trailing'] != null" #header-trailing="args">
                  <slot name="header-trailing" v-bind="args" />
                </template>
                <template
                  v-if="$slots['header-trailing-after'] != null"
                  #header-trailing-after="args"
                >
                  <slot name="header-trailing-after" v-bind="args" />
                </template>
              </InputFrameHeader>
            </template>
            <InputFrameLayout>
              <template v-if="$slots['common-config'] != null" #common-config>
                <slot name="common-config" :validation="validation" :save="save" />
              </template>
              <template v-if="$slots['side-navigation'] != null" #side-navigation>
                <slot name="side-navigation" :validation="validation" :save="save" />
              </template>
              <slot :validation="validation" :save="save">
                <ProgressSpinner v-if="loading" />
              </slot>
              <template v-if="$slots.settings != null" #settings>
                <slot name="settings" :validation="validation" :save="save" />
              </template>
            </InputFrameLayout>
            <template #footer="{ ok }">
              <template v-if="footerless"><slot name="footer"></slot></template>
              <InputFrameFooter
                v-else
                :extra-validation-rules="extraValidationRules"
                :warning="warning"
                :loading="loading || disableForm"
                :validation="validation"
                :ok-disabled="okDisabled"
                :delete-tooltip-content="deleteTooltipContent"
                :delete-label="deleteLabel"
                :prevent-delete="preventDelete"
                :delete-able="deleteAble"
                :create="create"
                :right-padding="false"
                :bottom-padding="false"
                :ok-button-variant="okButtonVariant"
                :test-prefix="testPrefix"
                :ok-only="okOnly"
                @delete-entity="deleteEntity"
                @back="back"
                @submit="ok"
              >
                <template v-for="(_, slot) of $slots" #[slot]="scope">
                  <slot :name="slot" v-bind="scope" />
                </template>
              </InputFrameFooter>
            </template>
          </LModal>
          <template v-else>
            <div
              class="input-frame-content px-2"
              :class="{ 'overflow-auto': contentScrollable, 'overflow-hidden': !contentScrollable }"
            >
              <InputFrameLayout>
                <template v-if="$slots['common-config'] != null" #common-config>
                  <slot name="common-config" :validation="validation" :save="save" />
                </template>
                <template v-if="$slots['side-navigation'] != null" #side-navigation>
                  <slot name="side-navigation" :validation="validation" :save="save" />
                </template>
                <slot :validation="validation" :save="save">
                  <ProgressSpinner v-if="loading" />
                </slot>
                <template v-if="$slots.settings != null" #settings>
                  <slot name="settings" :validation="validation" :save="save" />
                </template>
              </InputFrameLayout>
            </div>
            <template v-if="!footerless">
              <hr v-if="!hideHR" class="w-100" />
              <InputFrameFooter
                :extra-validation-rules="extraValidationRules"
                :warning="warning"
                :loading="loading || disableForm"
                :validation="validation"
                :ok-disabled="okDisabled"
                :delete-tooltip-content="deleteTooltipContent"
                :delete-label="deleteLabel"
                :prevent-delete="preventDelete"
                :delete-able="deleteAble"
                :create="create"
                :ok-button-variant="okButtonVariant"
                :test-prefix="testPrefix"
                class="px-2"
                :ok-only="okOnly"
                @delete-entity="deleteEntity"
                @back="back"
                @submit="save"
              >
                <template v-for="(_, slot) of $slots" #[slot]="scope">
                  <slot :name="slot" v-bind="scope" />
                </template>
              </InputFrameFooter>
            </template>
            <slot v-else name="footer"></slot>
          </template>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ProgressSpinner from "primevue/progressspinner";
import { type RouteLocationRaw, useRouter } from "vue-router";
import { type PropType, computed, nextTick, provide, watch } from "vue";
import InputFrameHeader from "./InputFrameHeader.vue";
import MobileHeaderTitle from "@/components/shared/MobileHeaderTitle.vue";
import InputFrameFooter from "./InputFrameFooter.vue";
import { InputFrameInjectionKey, InputFrameMode } from "./InputFrameMode";
import { confirmModal } from "@/components/shared/form/ConfirmationModal";
import LModal from "@/components/LModal.vue";
import { confirmDiscardChanges, useUnsavedChanges } from "@/functions/useUnsavedChanges";
import { type SubmitHandler, type SubmitResult, useFormValidation } from "@/modules/formValidation";
import { runConfirmedDelete } from "./InputFrameDeleteTransaction";
import type { ExtraValidationRule } from "./ExtraValidationRule";
import InputFrameLayout from "./InputFrameLayout.vue";
defineSlots();

const props = defineProps({
  title: {
    type: String,
    required: false,
    default: null,
  },
  hideHeader: {
    type: Boolean,
    required: false,
    default: false,
  },
  modalInBoardStructure: {
    type: Boolean,
    required: false,
    default: false,
  },
  createTo: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    required: false,
    default: null,
  },
  submit: {
    type: Function as PropType<SubmitHandler>,
    required: false,
    default: undefined,
  },
  create: {
    type: Boolean,
    required: false,
    default: false,
  },
  deleteAble: {
    type: Boolean,
    required: false,
    default: false,
  },
  footerless: {
    type: Boolean,
    required: false,
    default: false,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
  baselineLoading: {
    type: Boolean,
    required: false,
    default: undefined,
  },
  deleteTooltipContent: {
    type: String,
    required: false,
    default: null,
  },
  deleteLabel: {
    type: String,
    required: false,
    default: undefined,
  },
  deleteModalContent: {
    type: String,
    required: false,
    default: undefined,
  },
  deleteModalGroup: {
    type: String,
    required: false,
    default: undefined,
  },
  preventDelete: {
    type: Boolean,
    required: false,
    default: false,
  },
  warning: {
    type: Object as PropType<{ variant?: string; icon?: string; text: string }>,
    required: false,
    default: null,
  },
  extraValidationRules: {
    type: Array as PropType<readonly ExtraValidationRule[]>,
    required: false,
    default: () => [],
  },
  displayMode: {
    type: Number as PropType<InputFrameMode>,
    required: false,
    default: InputFrameMode.default,
  },
  disableForm: {
    type: Boolean,
    required: false,
    default: false,
  },
  disableCreate: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideLabel: {
    type: Boolean,
    required: false,
    default: false,
  },
  okButtonVariant: {
    type: String,
    required: false,
    default: "p-button-success",
  },
  okOnly: {
    type: Boolean,
    required: false,
    default: false,
  },
  okDisabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  testPrefix: {
    type: String,
    required: false,
    default: "",
  },
  hideHeaderOnPrint: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideHR: {
    type: Boolean,
    required: false,
    default: false,
  },
  contentScrollable: {
    type: Boolean,
    required: false,
    default: true,
  },
  wrapHeaderActions: {
    type: Boolean,
    required: false,
    default: true,
  },
  ignoreDiscardChangesConfirmation: {
    type: Boolean,
    required: false,
    default: false,
  },
});
const emit = defineEmits(["cancel", "delete", "click:create", "click:hiddenFeature"]);
const router = useRouter();
const baselineLoading = computed(() => props.baselineLoading ?? props.loading);
const validation = useFormValidation({
  name: () => `InputFrame:${props.title ?? ""}`,
  displayName: () => props.title ?? "",
  loading: baselineLoading,
  disabled: () => props.disableForm,
  propagateValidation: () => true,
});

useUnsavedChanges({
  isDirty: computed(() => !props.ignoreDiscardChangesConfirmation && validation.dirty),
});

watch(baselineLoading, async (isLoading, wasLoading) => {
  if (props.baselineLoading == null || isLoading === wasLoading) {
    return;
  }

  if (isLoading) {
    await validation.beginBaselineSession();
    return;
  }

  // Loading can finish before the loaded model has propagated through the fields.
  // Establish the baseline after that render.
  await nextTick();
  await validation.beginBaselineSession();
});

async function save() {
  if (props.displayMode === InputFrameMode.modal || !validation.canSubmit || props.submit == null) {
    return;
  }

  const result: SubmitResult = await props.submit();
  if (!result.success) {
    return;
  }
  await validation.commitBaseline();
  await result.afterCommit?.();
}

provide(
  InputFrameInjectionKey,
  computed(() => ({ disable: props.disableForm, submit: save })),
);

async function deleteEntity() {
  confirmModal({
    accept: async () => {
      await runConfirmedDelete(validation, () => emit("delete"));
    },
    showDeleteText: true,
    message: props.deleteModalContent,
    group: props.deleteModalGroup,
  });
}

async function back() {
  if (validation.dirty) {
    if (
      props.displayMode !== InputFrameMode.modal &&
      props.displayMode !== InputFrameMode.noFooterPadding
    ) {
      router.back();
      return;
    }

    if (!props.ignoreDiscardChangesConfirmation && (await confirmDiscardChanges())) {
      emit("cancel");
    }
  } else if (props.displayMode === InputFrameMode.modal) {
    emit("cancel");
  } else if (props.displayMode === InputFrameMode.noFooterPadding) {
    emit("cancel");
  } else {
    router.back();
  }
}

const showModal = computed({
  get() {
    return true;
  },
  set(visible: boolean) {
    if (visible === false) {
      emit("cancel");
    }
  },
});

defineExpose(validation);
</script>

<style lang="scss" scoped>
.h-0 {
  height: 0 !important;
}

.mainGrid {
  display: flex;
  height: 100%;
  min-height: 0;
  align-content: stretch;
  flex-direction: column;
}

.innerGrid {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  align-content: stretch;
}

.input-frame-content {
  flex: 1 1 auto;
  min-height: 0;
}

.stretching {
  flex: 1 1 auto;
  min-height: 0;
}

.NotStretching {
  flex: 1 1 auto;
  min-height: 0;
}
</style>
